import { Link } from 'react-router-dom';
import { Heart, CheckCircle2, Users, Briefcase, TrendingUp, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';

const placementServices = [
  'Strategic intake and assessment',
  'Resume positioning and interview preparation',
  'Coaching and structured support',
  'Employer outreach and relationship management',
  'Coordinated onboarding and retention follow-up',
];

const investmentOpportunities = [
  {
    amount: '$25,000',
    description: 'Fund one full placement pathway from intake to long-term employment.',
  },
  {
    amount: '$10,000',
    description: 'Support coaching, preparation, and employer engagement services.',
  },
  {
    amount: '$5,000',
    description: 'Expand employer outreach and recruiter partnerships.',
  },
  {
    amount: '$1,600/month',
    description: 'Sponsor ongoing psychologist-led support programming.',
  },
];

const outcomes = [
  { value: '150+', label: 'Employer Partners' },
  { value: '95%', label: 'Retention Rate' },
  { value: 'Millions', label: 'In Restored Earning Power' },
];

export default function Donate() {
  return (
    <div data-testid="donate-page">
      {/* Hero Section */}
      <section className="relative bg-slate-800 overflow-hidden pt-40 md:pt-[calc(12rem+10vh)] pb-12 md:pb-16" data-testid="donate-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700" />
        
        <div className="relative container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="max-w-3xl">
            <h1 
              className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-5 animate-fade-in-up md:whitespace-nowrap"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Invest in Employment<br className="md:hidden" /> <span className="text-purple-200 italic">That Lasts</span>
            </h1>
            <p className="text-base md:text-lg text-slate-300 mb-8 leading-relaxed animate-fade-in-up animation-delay-100">
              Aim Hire connects experienced professionals with meaningful work—strengthening families, 
              organizations, and communities in the process. Your support fuels the structured system 
              that makes every placement possible.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-200">
              <a href="#donate-form">
                <Button 
                  className="bg-white hover:bg-slate-100 text-slate-900 rounded-full px-10 py-7 text-xl font-medium"
                  data-testid="hero-donate-btn"
                >
                  Donate Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Form Section */}
      <section id="donate-form" className="py-16 md:py-20 bg-white scroll-mt-32" data-testid="donate-form-section">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-4xl">
          <div className="text-center mb-8">
            <h2 
              className="text-2xl md:text-3xl font-bold text-slate-900 mb-3"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Make a Donation
            </h2>
            <p className="text-base text-slate-600">
              Your tax-deductible gift supports employment pathways for experienced professionals.
            </p>
          </div>
          
          <div className="bg-slate-50 rounded-xl p-2 md:p-4 overflow-hidden">
            <iframe 
              src="https://www.zeffy.com/en-US/embed/donation-form/trusted-recruitment-a-meaningful-mission" 
              width="100%" 
              height="700" 
              frameBorder="0"
              title="Donation Form"
              className="rounded-lg min-h-[500px] md:min-h-[600px]"
              data-testid="donation-iframe"
              allow="payment"
              style={{ minHeight: '500px' }}
            />
          </div>
        </div>
      </section>

      {/* Why Your Support Matters */}
      <section className="py-16 md:py-20 bg-white" data-testid="why-support">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <h2 
                className="text-2xl md:text-3xl font-bold text-slate-900 mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Why Your Support Matters
              </h2>
              <p className="text-base text-slate-600 mb-5">
                Every successful placement is the result of intentional, professional work:
              </p>
              <ul className="space-y-3">
                {placementServices.map((service, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-700">{service}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-slate-50 p-6 md:p-8 rounded-xl">
              <p className="text-slate-700 text-base mb-3">Aim Hire does not charge candidates.</p>
              <p className="text-slate-700 text-base mb-3">We do not charge employers.</p>
              <p className="text-slate-900 font-semibold text-lg">
                Donor support sustains the entire employment pathway.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What It Takes */}
      <section className="py-16 md:py-20 bg-slate-900" data-testid="what-it-takes">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="max-w-2xl mx-auto text-center">
            <h2 
              className="text-2xl md:text-3xl font-bold text-white mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              What It Takes
            </h2>
            <p className="text-base text-slate-300 mb-4">
              Behind every placement is a structured system designed for long-term success.
            </p>
            <p className="text-sm text-slate-400 mb-5">
              The full placement pathway—including assessment, preparation, coaching, employer 
              engagement, and follow-up—represents an investment of approximately <span className="text-white font-semibold">$25,000</span>.
            </p>
            <p className="text-purple-200 font-medium text-base">
              Your gift strengthens that system and makes future placements possible.
            </p>
          </div>
        </div>
      </section>

      {/* Impact Investment Opportunities */}
      <section className="py-16 md:py-20 bg-white" data-testid="investment-opportunities">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="text-center mb-10">
            <h2 
              className="text-2xl md:text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Impact Investment Opportunities
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {investmentOpportunities.map((opportunity, index) => (
              <div 
                key={index}
                className="bg-slate-50 p-5 rounded-xl border border-slate-100 card-hover"
                data-testid={`investment-${index}`}
              >
                <div 
                  className="text-xl font-bold text-slate-900 mb-2"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {opportunity.amount}
                </div>
                <p className="text-xs text-slate-600">{opportunity.description}</p>
              </div>
            ))}
          </div>

          {/* Quick Donation Amounts */}
          <div className="bg-slate-50 p-6 md:p-10 rounded-xl text-center">
            <h3 
              className="text-xl font-semibold text-slate-900 mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Support the Work at Any Level
            </h3>
            <p className="text-sm text-slate-600 mb-6">Every contribution strengthens the employment pathway.</p>
            
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              <a href="#donate-form">
                <Button
                  className="rounded-full px-8 py-4 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium"
                >
                  Donate Now
                </Button>
              </a>
            </div>
            
            <p className="text-sm text-slate-600 italic">
              Your support restores stability to families and strengthens the broader community economy.
            </p>
          </div>
        </div>
      </section>

      {/* Measurable Outcomes */}
      <section className="py-16 md:py-20 bg-slate-50" data-testid="outcomes">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="text-center mb-10">
            <h2 
              className="text-2xl md:text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Measurable Outcomes
            </h2>
          </div>

          <div className="grid grid-cols-3 gap-6 mb-8 max-w-2xl mx-auto">
            {outcomes.map((outcome, index) => (
              <div key={index} className="text-center">
                <div 
                  className="text-2xl md:text-3xl font-bold text-slate-900 mb-1"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {outcome.value}
                </div>
                <div className="text-xs text-slate-600">{outcome.label}</div>
              </div>
            ))}
          </div>
          
          <p className="text-center text-sm text-slate-600">
            Each placement represents stability, contribution, and long-term impact.
          </p>
        </div>
      </section>

      {/* Partner With Us */}
      <section className="py-16 md:py-20 bg-slate-900" data-testid="partner-cta">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="text-center">
            <Heart className="w-10 h-10 text-purple-200 mx-auto mb-4" />
            <h2 
              className="text-2xl md:text-3xl font-bold text-white mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Partner With Us
            </h2>
            <p className="text-sm text-slate-300 mb-2 max-w-xl mx-auto">
              Aim Hire is a registered 501(c)(3) nonprofit organization.
            </p>
            <p className="text-xs text-slate-400 mb-8">
              Donations are tax-deductible to the fullest extent allowed by law.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                className="bg-white hover:bg-slate-100 text-slate-900 rounded-full px-8 py-5 text-sm font-medium"
                data-testid="final-donate-btn"
                onClick={() => alert('Donation functionality coming soon! Please contact info@aim-hire.org to donate.')}
              >
                Donate Now
              </Button>
              <Link to="/contact" data-testid="contact-partnership-btn">
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-5 text-sm font-medium"
                >
                  Contact Us to Discuss Partnership
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
